import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { LegalTocSection, LegalTocComponent } from '../../core/components/legal-toc/legal-toc.component';
import { LanguageService } from '../../core/core-services/language.service';
import { SeoService } from '../../core/services/seo.service';
import { AnnexProduct, ANNEX_PRODUCTS, ResolvedProductDoc, allPrivacyDocs } from '../legal/product-legal.registry';
import { buildProductToc } from '../legal/legal-toc-builder';
import { EU_REPRESENTATIVE, UK_REPRESENTATIVE, hasContact } from '../legal-entity';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';

/** Section count of the template's base card stack — keep in sync when adding/removing a card. */
const SECTION_COUNT = 23;

@Component({
    selector: 'app-privacy',
    templateUrl: './privacy.page.html',
    styleUrls: ['./privacy.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PortalNavbarComponent, IonicModule, LegalTocComponent, RouterLink, PortalFooterComponent, TranslatePipe]
})
export class PrivacyPage implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(LegalTocComponent) private toc?: LegalTocComponent;

    /** Products with a short annex card, always rendered on top of the base set —
     *  see docs/KONZEPT-Produkt-Rechtsseiten.md for why this page no longer switches
     *  on a `?product=` parameter. */
    readonly annexProducts: readonly AnnexProduct[] = ANNEX_PRODUCTS;

    /** Self-contained policies (ScreenDeck, Chroniq), always rendered as their own
     *  sections after the base card stack — see product-legal.registry.ts. */
    productDocs: readonly ResolvedProductDoc[] = [];

    /** Drives the header variant: app-portal-navbar while signed out; signed in, the
     *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
    isAuthenticated = false;

    /** Entries of the section index (app-legal-toc): the base card numbering, then
     *  one entry per section of every standalone product document. Card N carries
     *  id="ds-sNN" and the heading 'mainauth.privacy.sNNTitle'; a product section
     *  carries id="ds-<product>-s<num>" and its own plain-text title. */
    readonly baseTocSections: readonly LegalTocSection[] = Array.from({ length: SECTION_COUNT }, (_, i) => {
        const num = String(i + 1).padStart(2, '0');
        return { id: `ds-s${num}`, num, titleKey: `mainauth.privacy.s${num}Title` };
    });

    tocSections: readonly LegalTocSection[] = this.baseTocSections;

    /** Art. 27 GDPR / UK GDPR representatives — only rendered once appointed. */
    readonly euRep = EU_REPRESENTATIVE;
    readonly ukRep = UK_REPRESENTATIVE;
    readonly hasEuRep = hasContact(EU_REPRESENTATIVE);
    readonly hasUkRep = hasContact(UK_REPRESENTATIVE);

    private readonly destroy$ = new Subject<void>();
    private readonly titleKey = 'mainauth.privacy.navTitle';
    private readonly metaKey = 'mainauth.privacy.meta';
    /**
     * ?lang= — the language the reader was already using on the linking platform.
     * Only pins the document until the reader picks a language themselves; cleared on the
     * first onLangChange so the switcher is never dead against a URL parameter.
     */
    private requestedLang: string | null;

    /** ?product= is gone, but a plain #fragment (e.g. from an external app's
     *  "#tablestack" link) still has to land the reader on that section — see
     *  ngAfterViewInit and legal-toc.component.ts's scrollToId(). */
    private readonly fragment: string | null;

    constructor(
        route: ActivatedRoute,
        private readonly titleService: Title,
        private readonly meta: Meta,
        private readonly seo: SeoService,
        private readonly translate: TranslateService,
        private readonly language: LanguageService,
        private readonly portalAuth: PortalAuthService,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.requestedLang = route.snapshot.queryParamMap.get('lang');
        this.fragment = route.snapshot.fragment;
        this.applyProductDocs();
    }

    ngAfterViewInit(): void {
        if (this.fragment) {
            void this.toc?.scrollToId(this.fragment);
        }
    }

    ngOnInit(): void {
        // <title>/<meta name="description"> (docs/KONZEPT-Produkt-Rechtsseiten.md).
        // translated$() never yields raw i18n keys, so a direct/crawler landing doesn't get them as
        // its title, and it re-runs on a language switch and when a translation file arrives late.
        this.seo.translated$([this.titleKey, this.metaKey]).pipe(takeUntil(this.destroy$))
            .subscribe(t => this.applySeo(t));
        this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.applyProductDocs();
            this.cdr.markForCheck();
        });

        // A reader who works the switcher wants this page in that language, so the URL
        // parameter stops deciding. Without this an fr/it product section would stay
        // pinned for the rest of the visit and the switcher would look broken here alone.
        this.language.userSelection$.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.requestedLang = null;
        });

        if (this.requestedLang && this.language.languages.includes(this.requestedLang)) {
            this.language.useForSession(this.requestedLang);
            this.requestedLang = null;
        }

        this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
            this.isAuthenticated = auth;
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** DOM id of a standalone product's section. Used by both the template and the
     *  index, so the two cannot drift; namespaced by product because ScreenDeck and
     *  Chroniq both number their sections '01', '02', … independently. */
    sectionId(product: string, num: string): string {
        return `ds-${product}-s${num.toLowerCase().replace(/[^a-z0-9]/g, '')}`;
    }

    /** Resolves every standalone product document and rebuilds the section index. */
    private applyProductDocs(): void {
        this.productDocs = allPrivacyDocs(this.requestedLang, this.translate.currentLang);
        this.tocSections = buildProductToc({
            generalKey: 'mainauth.privacy.title',
            baseSections: this.baseTocSections,
            docs: this.productDocs,
            sectionId: (product, num) => this.sectionId(product, num),
            annexTitleKey: product => `mainauth.privacy.product.${product}.title`,
        });
    }

    private applySeo(t: Record<string, string>): void {
        this.titleService.setTitle(`${t[this.titleKey]} | Gastro Management`);
        this.meta.updateTag({ name: 'description', content: truncate(t[this.metaKey]) });
    }
}

function truncate(text: string, max = 155): string {
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
